import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { setDisplayTimezone, setPlatformDateFormat, type DatePattern } from './format';

/**
 * Loads the platform's date format and applies it app-wide.
 *
 * Called once, from each shell. It reads the same public `site-config` the
 * marketing site reads, so a Super Admin's choice reaches the console, the
 * landing site and every export without a deploy.
 *
 * The format is applied by setting module state in `format.ts` rather than by
 * providing a context — see the note there. What this hook adds is the fetch,
 * the cache and the re-render: `setPlatformDateFormat` on its own would change
 * the value without telling React that forty already-rendered dates are now
 * stale, so the version counter below forces one repaint when it lands.
 */

const PATTERNS: DatePattern[] = [
  'DD/MM/YYYY',
  'DD/MM/YY',
  'DD MMM YYYY',
  'MM/DD/YYYY',
  'YYYY-MM-DD',
];

function isPattern(value: unknown): value is DatePattern {
  return typeof value === 'string' && PATTERNS.includes(value as DatePattern);
}

const API_BASE = import.meta.env['VITE_API_BASE_URL'] ?? '';

interface SiteConfigResponse {
  general?: { dateFormat?: string; timezone?: string };
}

/**
 * @param timezone The zone dates should render in, when the caller knows a
 *   better one than the platform's — the admin shell passes the society's.
 *   Until it arrives, the platform's applies.
 */
export function useDateFormatSetting(timezone?: string | null): void {
  const [, bumpVersion] = React.useReducer((count: number) => count + 1, 0);

  const config = useQuery({
    queryKey: ['platform', 'site-config'],
    queryFn: async () => {
      // Not through `api.get`: this endpoint is public and lives outside
      // `/api/v1`, so it has no envelope-unwrapping to share.
      // `no-store`: the response carries a 60-second public cache for the
      // marketing site, and the console must not sit behind it — a branding
      // change saved here has to show on the next refetch, not a minute later.
      const response = await fetch(`${API_BASE}/public/site-config`, { cache: 'no-store' });
      if (!response.ok) throw new Error('site-config unavailable');

      const payload = (await response.json()) as { success: boolean; data?: SiteConfigResponse };
      return payload.data ?? {};
    },
    // It changes when someone edits a setting, which is rare.
    staleTime: 5 * 60_000,
    // A failure here must not retry in a loop behind every screen; the app
    // simply keeps its built-in format.
    retry: false,
  });

  const pattern = config.data?.general?.dateFormat;
  const platformTimezone = config.data?.general?.timezone;
  const effectiveTimezone = timezone || platformTimezone;

  React.useEffect(() => {
    if (isPattern(pattern)) setPlatformDateFormat(pattern);
    setDisplayTimezone(effectiveTimezone);
    // One repaint, so dates rendered before the fetch landed are rewritten.
    bumpVersion();
  }, [pattern, effectiveTimezone]);
}
